import Box from '@mui/material/Box'
import ToastComponent from 'components/toast'
import { useEffect, useState } from 'react'
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom'
import { defaultPhoneNumberCountry, setSessionItem } from 'utils/constants'
import { getSchool, verifySchoolCode } from 'utils/schools'
import { MessageProps } from 'utils/types'

import LinearProgress from '@mui/material/LinearProgress'
import InputMobileComponent from 'components/input-mobile'
import WizardComponent from 'components/wizard'
import { formatIncompletePhoneNumber, parsePhoneNumber } from 'libphonenumber-js/max'
import { isValidPhone } from 'utils/validations'
import './index.css'

function Index(): JSX.Element {
  const navigate: NavigateFunction = useNavigate()

  // Inputs: School Name
  const [searchParams] = useSearchParams()
  const schoolName: string = searchParams.get('sn') || ''

  // states
  const [schoolAdmins, setSchoolAdmins] = useState<string[]>([])
  const [schoolScheduleGroupNames, setSchoolScheduleGroupNames] = useState<string[]>([])
  const [schoolStatus, setSchoolStatus] = useState<string>('')
  const [isSchoolCode, setIsSchoolCode] = useState<boolean>(false)
  const [accessCode, setAccessCode] = useState<string>('') // could be school access code for open enrollement OR user access code for opt-in registration
  const [userPhoneNumber, setUserPhoneNumber] = useState<string>('')
  const [msg, setMsg] = useState<MessageProps>()
  const formattedPhoneNumber: string = formatIncompletePhoneNumber(userPhoneNumber, defaultPhoneNumberCountry)
  const e164PhoneNumber: string = isValidPhone(userPhoneNumber, true)
    ? parsePhoneNumber(userPhoneNumber, defaultPhoneNumberCountry).format('E.164')
    : ''

  // hooks
  useEffect(() => {
    (async function () {
      const { msg, data, error } = await getSchool(schoolName)
      if (error) setMsg(msg)
      else if (data) {
        setSchoolStatus(data.status as string)
        setIsSchoolCode(data.isSchoolCode as boolean)
        setSchoolAdmins(data?.schoolAdmins as string[])
        setSchoolScheduleGroupNames(
          (data?.schoolScheduleGroups as any[])?.map((group: any) => group.schoolScheduleGroupName),
        )
      }
    })()
  }, [schoolName])

  // handlers
  const onBack = () => navigate(-1)
  const onNext = async (): Promise<any> => {
    // Open enrollment code verification
    const { msg, error } = await verifySchoolCode(schoolName, accessCode, e164PhoneNumber)
    if (error) {
      setMsg(msg)
      return
    }
    // Save school name and code for following steps
    setSessionItem('x-pool-sn', schoolName)
    setSessionItem('x-pool-sa', schoolAdmins)
    setSessionItem('x-pool-sc', accessCode)
    setSessionItem('x-pool-pn', e164PhoneNumber)
    setSessionItem('x-pool-ssgn', schoolScheduleGroupNames)
    if (schoolScheduleGroupNames.length > 1) {
      navigate('/signup/school/schedule')
    } else {
      setSessionItem('x-pool-sg', schoolScheduleGroupNames[0])
      navigate('/signup/createUser')
    }
  }
  if (schoolStatus === 'enabled' && schoolAdmins?.length > 0) {
    const isValid: boolean = !!accessCode && (isSchoolCode || isValidPhone(userPhoneNumber, true))
    return (
      <WizardComponent
        onBack={onBack}
        title={'Enter Your Access Code'}
        disable={!isValid}
        btnText={'Next'}
        onClick={onNext}
        data_testId={'next-btn-testid'}
      >
        <Box display='flex' flexDirection='column' justifyContent='center' paddingTop={2} gap={4}>
          <InputMobileComponent
            type='text'
            labeltext={
              isSchoolCode ? 'School Access Code for Open Enrollment' : 'User Access Code for Restricted Enrollment'
            }
            helperText={`No access code? Contact your school admin at ${schoolAdmins[0]}`}
            data_testid='school_verification_code_testid'
            onChange={(v: string) => setAccessCode(v.trimEnd())}
          />
          {!isSchoolCode && (
            <InputMobileComponent
              type='tel'
              labeltext='Phone Number'
              isRequired={true}
              isError={!isValidPhone(userPhoneNumber)}
              helperText={
                !isValidPhone(userPhoneNumber)
                  ? 'A valid 10-digit mobile number is required'
                  : 'Access code tied to this phone number'
              }
              onChange={(v: string) => setUserPhoneNumber(v)}
              value={isValidPhone(userPhoneNumber) ? formattedPhoneNumber : userPhoneNumber}
            />
          )}
        </Box>

        {msg && (
          <ToastComponent
            style={msg?.style}
            heading={msg?.heading}
            text={msg?.text}
            onClose={() => setMsg(undefined)}
          />
        )}
      </WizardComponent>
    )
  } else if (schoolStatus === 'disabled') {
    return (
      <WizardComponent
        onBack={onBack}
        title={'School Not Enrolled'}
        subtitle={`Ask school/pta to enroll for carpool.school service.`}
      >
        <></>
      </WizardComponent>
    )
  } else if (schoolStatus === 'missing') {
    return (
      <WizardComponent
        onBack={onBack}
        title={'School Missing'}
        subtitle={`Contact us at support@carpool.school to add your school.`}
      >
        <></>
      </WizardComponent>
    )
  } else return <LinearProgress />
}

export default Index
